import React, {useRef, useState, useMemo, useEffect} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {formatLikeCount, movieTitleHandler} from "../../../utils/helpers";
import { ROUTES } from "../../../constants/routes";
import { genresData } from "../../../constants/structure";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import OverlayRatingIcon from "../../../components/icons/searchIcons/OverlayRatingIcon";
import OverlayTomatoRatingIcon from "../../../components/icons/searchIcons/OverlayTomatoRatingIcon";
import Comments from "../../../components/icons/reactionMenu/Comments";
import LikeIcon from "../../../components/icons/chat/likeIcon";
import BookMarkIcon from "../../../components/icons/chat/bookMarkIcon";
import styles from "./MovieCarousel.module.scss";
import RoomVideoPlayer from "../../room/components/RoomVideoPlayer";
import type {Swiper as SwiperClass} from "swiper";
import {setLastVisitedId} from "../../../store/slices/currentItem.slice";
import {useDispatch} from "react-redux";

const formatRating = (rating?: number | null) => (rating != null ? rating.toFixed(1) : undefined);

interface Movie {
  id: number;
  title: string;
  title_ru: string;
  title_ua: string;
  title_ar: string;
  poster_path: string;
  backdrop_path: string;
  trailer_key?: string | null;
  release_date: string;
  vote_average: number;
  vote_count: number;
  imdb_rating?: number | null;
  rotten_tomatoes_rating?: number | null;
  genre_ids: number[];
  overview: string;
  overview_ru: string;
  overview_ua: string;
  liked: boolean;
  in_favorite: boolean;
  shares_count: number;
  favorites_count: number;
  reviews_count?: number;
  comments_count?: number;
}

interface CarouselProps {
  movies: Movie[];
  muted: boolean;
  onToggleMuted: () => void;
  likeHandler: (e: any, item: any, liked: boolean) => void;
  favouritesHandler: (e: any, item: any, isFav: boolean) => void;
  likesList: number[];
  favoritesList: number[];
  updateVoteLocal: (id: any, key: string, type: string) => void;
  isMobile: boolean;
  openLoginModal: () => void;
  onShare: (id: number) => void;
}

const IMG_BASE = process.env.REACT_APP_IMG_BASE_URL;

const MovieCarouselMobile: React.FC<CarouselProps> = ({
                                                        movies,
                                                        muted,
                                                        onToggleMuted,
                                                        likeHandler,
                                                        favouritesHandler,
                                                        likesList,
                                                        favoritesList,
                                                        updateVoteLocal,
                                                        openLoginModal,
                                                        onShare,
                                                      }) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();

  const [activeIndex, setActiveIndex] = useState(0);
  const [brokenTrailerIds, setBrokenTrailerIds] = useState<Set<number>>(new Set());
  const swiperRef = useRef<SwiperClass | null>(null);

  const visibleMovies = useMemo(
    () => movies.filter(m => !brokenTrailerIds.has(m.id)),
    [movies, brokenTrailerIds]
  );

  const handleTrailerError = (movieId: number, movieIndex: number) => {
    setBrokenTrailerIds(prev => new Set(Array.from(prev).concat(movieId)));
    if (movieIndex <= activeIndex && activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleNavigateUp = () => {
    if (activeIndex > 0) swiperRef.current?.slideTo(activeIndex - 1);
  };

  const handleNavigateDown = () => {
    if (activeIndex < visibleMovies.length - 1) swiperRef.current?.slideTo(activeIndex + 1);
  };

  const windowStartRef = useRef(0);

  const formatCount = (value?: number) => {
    const count = value || 0;
    if (count >= 1000) return `${Math.round(count / 1000)}K`;
    return count;
  };

  const dispatch = useDispatch();

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || !swiper.pagination || !swiper.pagination.bullets) return;

    const maxVisible = 5;
    const total = visibleMovies.length;

    let start = windowStartRef.current;

    if (activeIndex < start) {
      start = activeIndex;
    } else if (activeIndex >= start + maxVisible) {
      start = activeIndex - maxVisible + 1;
    }

    start = Math.max(0, Math.min(start, Math.max(0, total - maxVisible)));
    windowStartRef.current = start;

    const end = Math.min(total, start + maxVisible);

    swiper.pagination.bullets.forEach((bullet: HTMLElement, index: number) => {
      bullet.style.display = index < start || index >= end ? "none" : "";
    });
  }, [activeIndex, visibleMovies.length]);



  return (
    <div className={styles.movieCarouselWrap}>
      <Swiper
        modules={[Pagination]}
        slidesPerView={1}
        spaceBetween={20}
        pagination={{
          clickable: true,
        }}
        edgeSwipeDetection={true}
        edgeSwipeThreshold={40}
        touchStartPreventDefault={false}
        className={styles.movieSwiper}
        onSwiper={(swiper: SwiperClass) => {
          swiperRef.current = swiper;
        }}
        onSlideChange={(swiper: SwiperClass) => {
          setActiveIndex(swiper.activeIndex);
        }}
      >
        {visibleMovies.map((movie, index) => {
          const title = movieTitleHandler(i18n.language, movie) || movie.title;
          const year = movie.release_date?.substring(0, 4);
          const genres = movie.genre_ids
            ?.slice(0, 2)
            .map((gid) => genresData.find((g) => g.id === gid)?.name)
            .filter(Boolean)
            .map((name) => t(name!))
            .join(", ");

          const primaryRating = formatRating(movie.imdb_rating ?? movie.vote_average) || "—";
          const rtRating = formatRating(movie.rotten_tomatoes_rating);
          const liked = likesList?.includes(movie.id);
          const isFav = favoritesList?.includes(movie.id);
          const commentsCount = movie.reviews_count ?? movie.comments_count ?? movie.shares_count ?? 0;

          const clickHandler = () => {
              navigate(`${ROUTES.DETAILS}/${movie.id}`);
              dispatch(setLastVisitedId(movie.id as number));
          };

          return (
            <SwiperSlide key={movie.id}>
              <div className={styles.movieCarouselSlide}>
                <div className={styles.movieCarouselCard}>
                  <div className={styles.movieCarouselVideo}>
                    <RoomVideoPlayer
                      trailerKey={index === activeIndex ? (movie.trailer_key ?? null) : null}
                      backdropPath={movie.backdrop_path}
                      muted={muted}
                      onToggleMuted={onToggleMuted}
                      isActive={index === activeIndex}
                      onTrailerError={() => handleTrailerError(movie.id, index)}
                      navigation={{
                        mode: "slides",
                        onNavigateUp: handleNavigateUp,
                        onNavigateDown: handleNavigateDown,
                        canNavigateUp: activeIndex > 0,
                        canNavigateDown: activeIndex < visibleMovies.length - 1,
                      }}
                    />
                  </div>
                  <div className={styles.movieCarouselInfo}>
                    <div className={styles.movieCarouselBlock} onClick={() => navigate(`${ROUTES.DETAILS}/${movie.id}`)}>
                      {movie.poster_path ? (
                        <div className={styles.movieCarouselImage}>
                          <img src={`${IMG_BASE}${movie.poster_path}`} alt={title} />
                        </div>
                      ) : (
                        <div className={styles.movieCarouselPosterPlaceholderSmall} />
                      )}

                      <div className={styles.movieCarouselBox}>
                        <h4 className={styles.movieCarouselSlideTitle}>{title}</h4>
                        <div className={styles.movieCarouselSlideMeta}>
                          {genres && <span className={styles.genres}>{genres}</span>}
                          {year && <span className={styles.year}>{year}</span>}
                        </div>
                        <div className={styles.ratings}>
                          <div className={styles.ratingItem}>
                            <OverlayRatingIcon />
                            <span className={styles.rating}>{primaryRating}</span>
                          </div>
                          {rtRating && (
                            <div className={styles.ratingItem}>
                              <OverlayTomatoRatingIcon />
                              <span className={styles.rating}>{rtRating}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className={styles.movieCarouselActions}>
                      <div
                        id="likesDetails"
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem("accessToken");
                          if (!token) {
                            openLoginModal();
                            return;
                          }
                          likeHandler(e, movie, liked);
                          updateVoteLocal(movie.id, "vote_count", liked ? "decrement" : "increment");
                        }}
                      >
                        <div className={`${styles.movieCarouselActionsIcon} ${liked ? styles.movieCarouselActionsIconActive : ""}`}>
                          <LikeIcon />
                        </div>
                        <div className={styles.movieCarouselActionsValue}>{movie.vote_count}</div>
                      </div>

                      <div
                        id="shareDetails"
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          clickHandler()
                        }}
                      >
                        <div className={styles.movieCarouselActionsIcon}>
                          <Comments />
                        </div>
                        <div className={styles.movieCarouselActionsValue}>{formatCount(commentsCount)}</div>
                      </div>

                      <div
                        id="savedDetails"
                        className={styles.movieCarouselActionsItem}
                        onClick={(e) => {
                          e.stopPropagation();
                          const token = localStorage.getItem("accessToken");
                          if (!token) {
                            openLoginModal();
                            return;
                          }
                          favouritesHandler(e, movie, isFav);
                          updateVoteLocal(movie.id, "favorites_count", isFav ? "decrement" : "increment");
                        }}
                      >
                        <div className={`${styles.movieCarouselActionsIcon} ${isFav ? styles.movieCarouselActionsIconActive : ""}`}>
                          <BookMarkIcon />
                        </div>
                        <div className={styles.movieCarouselActionsValue}>{formatLikeCount(movie.favorites_count, { compact: true })}</div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          );
        })}
      </Swiper>
    </div>
  );
};

export default MovieCarouselMobile;
